import { Button, Divider, FormControlLabel, Grid, Switch, TextField, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { withStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { Styles } from "src/styles/material-styles";
import { NotificationVariant } from "src/utils/enums/notification-variants.enum";
import { AI_SUGGESTION_DEFAULT_PROMPT } from "src/utils/ai-suggestion-default-prompt";
import * as LLMSuggestionSettingsService from "src/services/llm-suggestion-settings.service";

const DEFAULT_LIMITS = {
    calledmessageslimit: 20,
    calledrequesterprofilelimit: 5,
    calledchildcalledslimit: 20,
    calledrelatedcalledslimit: 5,
    calledtoneexampleslimit: 5,
    calledtechniciantonelimit: 5,
    chatmessageslimit: 30,
    chatrequesterprofilelimit: 5,
    chatchildcalledslimit: 20,
    chatrelatedcalledslimit: 5,
    chattoneexampleslimit: 5,
    chattechniciantonelimit: 5
};

const defaultItem = {
    enabled: false,
    prompt: "",
    calledmessagesenabled: true,
    calledmessageslimit: DEFAULT_LIMITS.calledmessageslimit,
    calledrequesterprofileenabled: true,
    calledrequesterprofilelimit: DEFAULT_LIMITS.calledrequesterprofilelimit,
    calledchildcalledsenabled: true,
    calledchildcalledslimit: DEFAULT_LIMITS.calledchildcalledslimit,
    calledrelatedcalledsenabled: true,
    calledrelatedcalledslimit: DEFAULT_LIMITS.calledrelatedcalledslimit,
    calledtoneexamplesenabled: true,
    calledtoneexampleslimit: DEFAULT_LIMITS.calledtoneexampleslimit,
    calledtechniciantoneenabled: true,
    calledtechniciantonelimit: DEFAULT_LIMITS.calledtechniciantonelimit,
    chatmessagesenabled: true,
    chatmessageslimit: DEFAULT_LIMITS.chatmessageslimit,
    chatrequesterprofileenabled: true,
    chatrequesterprofilelimit: DEFAULT_LIMITS.chatrequesterprofilelimit,
    chatchildcalledsenabled: false,
    chatchildcalledslimit: DEFAULT_LIMITS.chatchildcalledslimit,
    chatrelatedcalledsenabled: false,
    chatrelatedcalledslimit: DEFAULT_LIMITS.chatrelatedcalledslimit,
    chattoneexamplesenabled: false,
    chattoneexampleslimit: DEFAULT_LIMITS.chattoneexampleslimit,
    chattechniciantoneenabled: false,
    chattechniciantonelimit: DEFAULT_LIMITS.chattechniciantonelimit
};

const MODULES = [
    { key: "messages", labelId: "suggestion.settings.module.messages" },
    { key: "requesterprofile", labelId: "suggestion.settings.module.requester.profile" },
    { key: "childcalleds", labelId: "suggestion.settings.module.child.calleds" },
    { key: "relatedcalleds", labelId: "suggestion.settings.module.related.calleds" },
    { key: "toneexamples", labelId: "suggestion.settings.module.tone.examples" },
    { key: "techniciantone", labelId: "suggestion.settings.module.technician.tone" }
];

const ENABLED_SUGGESTION_HELP = "Quando ativado, o Mexx passa a sugerir até 3 respostas prontas (com abordagens diferentes: resolutiva, pedido de informação e atualização de status) toda vez que um atendente for responder um chamado ou uma conversa de chat dessa LLM. As sugestões são geradas por IA a partir do prompt abaixo e dos contextos configurados nesta tela, e o atendente pode usar, editar ou descartar cada uma antes de enviar.";

const resolveModuleHelp = (scope, module) => {
    if (module.key === "messages") {
        return scope === "called"
            ? "Inclui o histórico de acompanhamentos já registrados neste chamado (as respostas e anotações trocadas com o solicitante), do mais recente pro mais antigo, para a IA entender o andamento do atendimento antes de sugerir a próxima resposta."
            : "Inclui o histórico de mensagens já trocadas nesta conversa de chat, do mais recente pro mais antigo, para a IA entender o que já foi dito antes de sugerir a próxima resposta.";
    }

    switch (module.key) {
        case "requesterprofile":
            return "Inclui um resumo dos chamados já finalizados anteriormente pelo mesmo solicitante, para a IA identificar se é um problema recorrente ou se já teve uma solução parecida antes.";
        case "childcalleds":
            return "Inclui os chamados filhos abertos dentro deste mesmo atendimento. Enquanto algum chamado filho estiver em aberto, a IA evita sugerir que o atendimento como um todo já está concluído.";
        case "relatedcalleds":
            return "Inclui chamados que um atendente vinculou manualmente a este (o motivo do vínculo não é registrado pelo Mexx — pode ser um chamado duplicado, a mesma causa raiz ou só uma referência histórica). Serve como contexto adicional; a IA não assume que tratam do mesmo problema.";
        case "toneexamples":
            return "Inclui respostas padrão e macros já cadastradas no Mexx para este contratante, usadas somente como referência de estilo de escrita — a IA não repete o conteúdo delas, só imita o tom.";
        case "techniciantone":
            return "Inclui respostas anteriores escritas pelo próprio atendente que está gerando a sugestão, em chamados da mesma organização, para a IA imitar o jeito de escrever desse atendente específico, em vez de um tom genérico.";
        default:
            return "";
    }
};

const SuggestionConfiguration = (props) => {
    const { intl, llm, onShowNotification, onLoading } = props;

    const [item, setItem] = useState(defaultItem);

    const fetchSettings = async () => {
        onLoading(true);
        try {
            const output = await LLMSuggestionSettingsService.getByLLMId(llm.id);
            const data = output?.data;

            if (data) {
                setItem({
                    enabled: data.enabled ?? false,
                    prompt: data.prompt ?? "",
                    calledmessagesenabled: data.calledmessagesenabled ?? true,
                    calledmessageslimit: data.calledmessageslimit ?? DEFAULT_LIMITS.calledmessageslimit,
                    calledrequesterprofileenabled: data.calledrequesterprofileenabled ?? true,
                    calledrequesterprofilelimit: data.calledrequesterprofilelimit ?? DEFAULT_LIMITS.calledrequesterprofilelimit,
                    calledchildcalledsenabled: data.calledchildcalledsenabled ?? true,
                    calledchildcalledslimit: data.calledchildcalledslimit ?? DEFAULT_LIMITS.calledchildcalledslimit,
                    calledrelatedcalledsenabled: data.calledrelatedcalledsenabled ?? true,
                    calledrelatedcalledslimit: data.calledrelatedcalledslimit ?? DEFAULT_LIMITS.calledrelatedcalledslimit,
                    calledtoneexamplesenabled: data.calledtoneexamplesenabled ?? true,
                    calledtoneexampleslimit: data.calledtoneexampleslimit ?? DEFAULT_LIMITS.calledtoneexampleslimit,
                    calledtechniciantoneenabled: data.calledtechniciantoneenabled ?? true,
                    calledtechniciantonelimit: data.calledtechniciantonelimit ?? DEFAULT_LIMITS.calledtechniciantonelimit,
                    chatmessagesenabled: data.chatmessagesenabled ?? true,
                    chatmessageslimit: data.chatmessageslimit ?? DEFAULT_LIMITS.chatmessageslimit,
                    chatrequesterprofileenabled: data.chatrequesterprofileenabled ?? true,
                    chatrequesterprofilelimit: data.chatrequesterprofilelimit ?? DEFAULT_LIMITS.chatrequesterprofilelimit,
                    chatchildcalledsenabled: data.chatchildcalledsenabled ?? false,
                    chatchildcalledslimit: data.chatchildcalledslimit ?? DEFAULT_LIMITS.chatchildcalledslimit,
                    chatrelatedcalledsenabled: data.chatrelatedcalledsenabled ?? false,
                    chatrelatedcalledslimit: data.chatrelatedcalledslimit ?? DEFAULT_LIMITS.chatrelatedcalledslimit,
                    chattoneexamplesenabled: data.chattoneexamplesenabled ?? false,
                    chattoneexampleslimit: data.chattoneexampleslimit ?? DEFAULT_LIMITS.chattoneexampleslimit,
                    chattechniciantoneenabled: data.chattechniciantoneenabled ?? false,
                    chattechniciantonelimit: data.chattechniciantonelimit ?? DEFAULT_LIMITS.chattechniciantonelimit
                });
            }
        } catch (err) {
            onShowNotification(intl.formatMessage({ id: "process.error" }), NotificationVariant.ERROR);
        }
        onLoading(false);
    };

    useEffect(() => {
        if (llm?.id) {
            fetchSettings();
        }
    }, [llm]);

    const handleEnabledChange = (e) => {
        const checked = e.target.checked;
        setItem((prev) => ({
            ...prev,
            enabled: checked,
            prompt: checked && !prev.prompt?.trim() ? AI_SUGGESTION_DEFAULT_PROMPT : prev.prompt
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setItem((prev) => ({ ...prev, [name]: value }));
    };

    const handleModuleToggle = (scope, moduleKey) => (e) => {
        const checked = e.target.checked;
        setItem((prev) => ({ ...prev, [`${scope}${moduleKey}enabled`]: checked }));
    };

    const handleModuleLimitChange = (scope, moduleKey) => (e) => {
        const value = e.target.value;
        setItem((prev) => ({ ...prev, [`${scope}${moduleKey}limit`]: value }));
    };

    const toNullableInt = (value) => {
        if (value === "" || value === null || value === undefined) {
            return null;
        }
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed)) {
            return null;
        }
        return Math.max(0, parsed);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        onLoading(true);

        const payload = {
            enabled: item.enabled,
            prompt: item.prompt,
            dailyLimitPerUser: 0,
            calledMessagesEnabled: item.calledmessagesenabled,
            calledMessagesLimit: toNullableInt(item.calledmessageslimit),
            calledRequesterProfileEnabled: item.calledrequesterprofileenabled,
            calledRequesterProfileLimit: toNullableInt(item.calledrequesterprofilelimit),
            calledChildCalledsEnabled: item.calledchildcalledsenabled,
            calledChildCalledsLimit: toNullableInt(item.calledchildcalledslimit),
            calledRelatedCalledsEnabled: item.calledrelatedcalledsenabled,
            calledRelatedCalledsLimit: toNullableInt(item.calledrelatedcalledslimit),
            calledToneExamplesEnabled: item.calledtoneexamplesenabled,
            calledToneExamplesLimit: toNullableInt(item.calledtoneexampleslimit),
            calledTechnicianToneEnabled: item.calledtechniciantoneenabled,
            calledTechnicianToneLimit: toNullableInt(item.calledtechniciantonelimit),
            chatMessagesEnabled: item.chatmessagesenabled,
            chatMessagesLimit: toNullableInt(item.chatmessageslimit),
            chatRequesterProfileEnabled: item.chatrequesterprofileenabled,
            chatRequesterProfileLimit: toNullableInt(item.chatrequesterprofilelimit),
            chatChildCalledsEnabled: item.chatchildcalledsenabled,
            chatChildCalledsLimit: toNullableInt(item.chatchildcalledslimit),
            chatRelatedCalledsEnabled: item.chatrelatedcalledsenabled,
            chatRelatedCalledsLimit: toNullableInt(item.chatrelatedcalledslimit),
            chatToneExamplesEnabled: item.chattoneexamplesenabled,
            chatToneExamplesLimit: toNullableInt(item.chattoneexampleslimit),
            chatTechnicianToneEnabled: item.chattechniciantoneenabled,
            chatTechnicianToneLimit: toNullableInt(item.chattechniciantonelimit)
        };

        try {
            await LLMSuggestionSettingsService.update(llm.id, payload);
            onShowNotification(intl.formatMessage({ id: "suggestion.settings.saved" }));
        } catch (err) {
            onShowNotification(intl.formatMessage({ id: "process.error" }), NotificationVariant.ERROR);
        }
        onLoading(false);
    };

    const resolveModuleLabelId = (scope, module) => {
        if (scope === "called" && module.key === "messages") {
            return "suggestion.settings.module.accompaniments";
        }
        return module.labelId;
    };

    const renderModuleRow = (scope) => (module) => {
        const enabledKey = `${scope}${module.key}enabled`;
        const limitKey = `${scope}${module.key}limit`;

        const enabled = item[enabledKey];

        return (
            <Grid container item xs={12} spacing={2} alignItems="center" key={`${scope}-${module.key}`}>
                <Grid item xs={12} sm={enabled ? 7 : 12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={handleModuleToggle(scope, module.key)}
                                color="primary"
                            />
                        }
                        label={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {intl.formatMessage({ id: resolveModuleLabelId(scope, module) })}
                                <Tooltip title={resolveModuleHelp(scope, module)} arrow>
                                    <HelpOutlineIcon fontSize="small" style={{ color: "#9e9e9e", cursor: "help" }} />
                                </Tooltip>
                            </span>
                        }
                    />
                </Grid>
                {enabled && (
                    <Grid item xs={12} sm={5}>
                        <TextField
                            type="number"
                            fullWidth
                            label={intl.formatMessage({ id: "suggestion.settings.module.limit" })}
                            value={item[limitKey]}
                            onChange={handleModuleLimitChange(scope, module.key)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                )}
            </Grid>
        );
    };

    return (
        <form className={props.classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={item.enabled}
                                onChange={handleEnabledChange}
                                color="primary"
                            />
                        }
                        label={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {intl.formatMessage({ id: "field.accompaniment.suggestion.enabled" })}
                                <Tooltip title={ENABLED_SUGGESTION_HELP} arrow>
                                    <HelpOutlineIcon fontSize="small" style={{ color: "#9e9e9e", cursor: "help" }} />
                                </Tooltip>
                            </span>
                        }
                    />
                </Grid>

                {item.enabled && (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                multiline
                                rows={5}
                                fullWidth
                                variant="outlined"
                                name="prompt"
                                label={intl.formatMessage({ id: "field.accompaniment.suggestion.prompt" })}
                                value={item.prompt}
                                onChange={handleChange}
                                helperText={intl.formatMessage({ id: "helper.field.accompaniment.suggestion.prompt" })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                            <Typography variant="h6" style={{ marginTop: 16 }}>
                                <FormattedMessage id="suggestion.settings.section.called" />
                            </Typography>
                        </Grid>

                        {MODULES.map(renderModuleRow("called"))}

                        <Grid item xs={12}>
                            <Divider />
                            <Typography variant="h6" style={{ marginTop: 16 }}>
                                <FormattedMessage id="suggestion.settings.section.chat" />
                            </Typography>
                        </Grid>

                        {MODULES.map(renderModuleRow("chat"))}
                    </>
                )}

                <Grid item xs={12} sm={4}>
                    <Button type="submit" fullWidth variant="contained" color="primary">
                        <FormattedMessage id="save" />
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default injectIntl(withStyles(Styles)(SuggestionConfiguration));